import { useEffect, useMemo, useRef, useState } from 'react'
import { geoNaturalEarth1, geoPath } from 'd3-geo'
import { feature } from 'topojson-client'
import type { Feature, FeatureCollection, Geometry } from 'geojson'
import type { Topology, GeometryCollection } from 'topojson-specification'
import topologyUrl from 'world-atlas/countries-110m.json?url'
import { NUMERIC_TO_ALPHA2 } from '@/shared/places/worldNumeric'
import { countryName } from '@/shared/places/places'
import './WorldMap.css'

/**
 * Where the users are, as a map — 29 Oct 2026.
 *
 * A classed choropleth, the cartographers' standard (ColorBrewer's rule): each
 * country's share of the users falls in one of six classes, and each class is
 * a step of one blue ramp from light to dark — 29 Oct 2026. It replaced a
 * continuous 40–70 % opacity, on which 0.1 % and 40 % looked nearly alike.
 * The class breaks grow roughly fivefold, because the shares do: Norway
 * holds about half, and most countries hold a fraction of a per cent.
 * Countries nobody is in stay grey. Hovering shows the country, its count
 * and its share.
 *
 * The outlines are Natural Earth's (public domain) as packed by world-atlas,
 * fetched as a file of their own only when the card is shown. Countries too
 * small for the 1:110m scale (Andorra, Liechtenstein, Monaco …) are not drawn;
 * they are still in the pie and the totals.
 */
type Countries = FeatureCollection<Geometry, { name: string }>

const W = 960
const H = 470
/**
 * The classes, by share of the users, and their colours — the sequential
 * blue ramp of the dataviz palette, steps 150 to 650.
 */
export const CLASSES: { from: number; label: string; color: string }[] = [
  { from: 0, label: '< 0.1 %', color: '#b7d3f6' },
  { from: 0.001, label: '0.1–0.5 %', color: '#86b6ef' },
  { from: 0.005, label: '0.5–1 %', color: '#5598e7' },
  { from: 0.01, label: '1–5 %', color: '#2a78d6' },
  { from: 0.05, label: '5–20 %', color: '#1c5cab' },
  { from: 0.2, label: '20 % +', color: '#104281' },
]

const colourOf = (share: number) => [...CLASSES].reverse().find((c) => share >= c.from)!.color

let loading: Promise<Countries> | null = null
function loadCountries(): Promise<Countries> {
  loading ??= fetch(topologyUrl)
    .then((r) => r.json() as Promise<Topology<{ countries: GeometryCollection<{ name: string }> }>>)
    .then((t) => feature(t, t.objects.countries) as unknown as Countries)
  return loading
}

const codeOf = (f: Feature<Geometry, { name: string }>): string | null => {
  const id = String(f.id ?? '')
  if (id) return NUMERIC_TO_ALPHA2[id] ?? null
  return f.properties.name === 'Kosovo' ? 'XK' : null
}

export function WorldMap({
  counts,
  total,
  fill = false,
  noun = 'users',
}: {
  counts: Record<string, number>
  total: number
  /** What is being counted, for the tooltip: "users", "subscribers". */
  noun?: string
  /** Fill the height the layout gives it, rather than taking the map's own shape. */
  fill?: boolean
}) {
  const [countries, setCountries] = useState<Countries | null>(null)
  const [failed, setFailed] = useState(false)
  const [tip, setTip] = useState<{ x: number; y: number; code: string; name: string } | null>(null)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let live = true
    loadCountries()
      .then((c) => live && setCountries(c))
      .catch(() => live && setFailed(true))
    return () => {
      live = false
    }
  }, [])

  const drawn = useMemo(() => {
    if (!countries) return []
    const land = { ...countries, features: countries.features.filter((f) => f.properties.name !== 'Antarctica') }
    const path = geoPath(geoNaturalEarth1().fitSize([W, H], land))
    return land.features.map((f) => ({ f, code: codeOf(f), d: path(f) ?? '' }))
  }, [countries])

  const n = tip ? counts[tip.code] ?? 0 : 0

  if (failed) return <p className="empty-note">Could not load the map.</p>
  if (!countries) return <p className="empty-note">Drawing the map…</p>

  return (
    <div className={fill ? 'wmap wmap--fill' : 'wmap'} ref={box} onMouseLeave={() => setTip(null)}>
      {/* The drawing area. It sizes to the map's own shape by default; a
          layout that gives the map a height (the dashboard's wide row) can make
          it fill that instead — see `.wmap--fill`. */}
      <div className="wmap__area">
      <svg viewBox={`0 0 ${W} ${H}`} className="wmap__svg" role="img" aria-label="Users by country">
        {drawn.map(({ f, code, d }) => {
          const v = code ? counts[code] ?? 0 : 0
          return (
            <path
              key={String(f.id ?? f.properties.name)}
              d={d}
              className={v > 0 ? 'wmap__land wmap__land--users' : 'wmap__land'}
              style={v > 0 ? { fill: colourOf(v / Math.max(1, total)) } : undefined}
              onMouseMove={(e) => {
                const r = box.current?.getBoundingClientRect()
                if (!r) return
                setTip({
                  x: e.clientX - r.left,
                  y: e.clientY - r.top,
                  code: code ?? '',
                  name: code ? countryName(code) : f.properties.name,
                })
              }}
            />
          )
        })}
      </svg>
      </div>
      {tip && (
        <div
          className="wmap__tip"
          style={{
            left: Math.min(tip.x + 12, (box.current?.clientWidth ?? 0) - 170),
            top: tip.y + 12,
          }}
        >
          <strong>{tip.name}</strong>
          <span>
            {n > 0
              ? `${n.toLocaleString('nb-NO')} ${noun} · ${((n / Math.max(1, total)) * 100).toFixed(1)} %`
              : `No ${noun}`}
          </span>
        </div>
      )}
      <div className="wmap__legend" aria-label="Share of users">
        {CLASSES.map((c) => (
          <span className="wmap__class" key={c.label}>
            <span className="wmap__swatch" style={{ background: c.color }} />
            {c.label}
          </span>
        ))}
      </div>
    </div>
  )
}
